import { fetchEmailContactInAnotherPartner } from 'legacy/lut/components/widgets/public/onboarding/retailRegistration/registrationSteps/onboardingRetail/RoleHelper'
import { memo, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import {
  ContactWorkflowStatus,
  IContact,
  mapContactFromContactAllData,
} from 'clickNgo/entities/contact'
import { LoadingState } from 'clickNgo/entities/contact'
import { emptyContactFlags } from 'clickNgo/entities/contactFlag'
import { PartnerGeneralInfoPanel } from 'clickNgo/entities/partner/ui/partnerGeneralInfoPanel'
import { ProfilingCodeType } from 'clickNgo/entities/profilingCode'
import { useGetContainerContentHeightByOffset } from 'clickNgo/hooks'
import { InfoBlock, InfoBlockRounded, InfoBlockType } from 'cng-common/components/infoBlock'
import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { useGetFormDataField } from 'cng-common/features/form'
import { selectContactsWithMultiplePartnersData } from 'onboarding/features/selectors/selectCommonData'
import { SideMenu } from 'shared/components/sideMenu'

import { ContactFormTitle } from './components/contactFormTitle/ContactFormTitle'
import { MenuLabelWithInfo } from './components/menuLabelWithInfo'
import {
  WrappedContactAdditionalInfo,
  WrappedContactBasicInfo,
  WrappedContactFlags,
  WrappedContactRelinking,
} from './components/wrappedComponents'
import { WrappedOnboardingContactRelinking } from './components/wrappedComponents/wrappedContactRelinking/WrappedOnboardingContactRelinking'
import { WrappedCreateReplacementContact } from './components/wrappedComponents/wrappedCreateReplacementContact'
import { ContactEditorFormKeys } from './configs'
import { useContactWithMultiplePartners } from './hooks/useContactWithMultiplePartners'
import {
  ContactEditorSettingFormFields,
  ContactFormMenuItems,
  TContactEditorContextProps,
  TExistedContactProps,
  TNewContactProps,
} from './models'
import { contactEditorTranslation } from './translation'

import styles from './ContactEditorContent.module.scss'

type TProps = (TNewContactProps | TExistedContactProps) & TContactEditorContextProps

export const ContactEditorContent = memo<TProps>(
  ({
    contact,
    contactFlags = emptyContactFlags,
    source,
    requestPartnerId,
    isOnboarding = false,
    onClose,
    onContactUpdate,
    onContactFlagsUpdate,
    onReplacementContactCreate,
  }) => {
    const contactsWithMultiplePartnersData = useSelector(selectContactsWithMultiplePartnersData)
    const headerRef = useRef<HTMLDivElement>(null)
    const isNewContact = contact.contactId.startsWith('new-')
    const isAwaitingCreation = contact.workflowStatus === ContactWorkflowStatus.AwaitingCreation
    const isRelinkingFinished = Boolean(contact.newPartnerId)

    const { contentHeight, containerRef } = useGetContainerContentHeightByOffset(
      headerRef.current?.offsetHeight
    )
    const [currentMenuItem, setCurrentMenuItem] = useGetFormDataField<ContactFormMenuItems>(
      ContactEditorFormKeys.settings,
      ContactEditorSettingFormFields.CurrentMenuItem
    )
    const [loadingState] = useGetFormDataField<LoadingState>(
      ContactEditorFormKeys.settings,
      ContactEditorSettingFormFields.IsLoading
    )

    const basicInfoTitle = useMemo(
      () => <MenuLabelWithInfo title={contactEditorTranslation.menu.basic} modifiedValues={[]} />,
      []
    )

    const additionalInfoTitle = useMemo(
      () => (
        <MenuLabelWithInfo title={contactEditorTranslation.menu.additional} modifiedValues={[]} />
      ),
      []
    )

    const { isMultiplePartners } = useContactWithMultiplePartners(
      contact.email,
      contactsWithMultiplePartnersData.data
    )
    const [contactForRelinkingData, setContactForRelinkingData] = useState<IContact>({} as IContact)
    const [loadingContactForRelinking, setLoadingContactForRelinking] = useState(false)
    const resolvedContactPartnerId = requestPartnerId || contact.partnerId

    useEffect(() => {
      if (!isOnboarding || !isNewContact || !isMultiplePartners) {
        setContactForRelinkingData({} as IContact)
        setLoadingContactForRelinking(false)
        return
      }

      let isCancelled = false

      setLoadingContactForRelinking(true)

      fetchEmailContactInAnotherPartner(contact.partnerId, contact.email)
        .then((data) => {
          if (isCancelled) {
            return
          }

          const existingContact = mapContactFromContactAllData(data)
          setContactForRelinkingData({
            ...existingContact,
            newPartnerId: contact.partnerId,
            firstName: contact.firstName,
            lastName: contact.lastName,
            source: contact.source,
            onboardingRequestId: contact.onboardingRequestId,
          })
        })
        .finally(() => {
          if (!isCancelled) {
            setLoadingContactForRelinking(false)
          }
        })

      return () => {
        isCancelled = true
      }
    }, [
      contact.contactId,
      contact.email,
      contact.firstName,
      contact.lastName,
      contact.onboardingRequestId,
      contact.partnerId,
      contact.source,
      isMultiplePartners,
      isNewContact,
      isOnboarding,
    ])

    const isOnboardingSideMenu = Boolean(
      isOnboarding &&
        !isRelinkingFinished &&
        isNewContact &&
        isMultiplePartners &&
        !loadingContactForRelinking
    )

    return (
      <div ref={containerRef} className={styles.contactEditor}>
        <div className={styles.header} ref={headerRef}>
          <div className={styles.title}>
            <ContactFormTitle contact={contact} />
          </div>
          <PartnerGeneralInfoPanel
            communicationName={contact.communicationName}
            locationId={contact.locationId}
            partnerId={contact.partnerId}
            onboardingRequestId={contact.onboardingRequestId}
            hierarchyType={contact.hierarchyType}
          />
          <RenderWhen condition={isAwaitingCreation}>
            <InfoBlock
              text={contactEditorTranslation.warnings.awaitingCreation}
              type={InfoBlockType.Warning}
              rounded={InfoBlockRounded.None}
            />
          </RenderWhen>
        </div>
        <div className={styles.content} style={{ height: contentHeight }}>
          <LoaderOverlay
            isVisible={loadingState.isLoading}
            color="white"
            message={loadingState.message}
          />
          <SideMenu
            classes={styles}
            keepMounted
            onItemSelectById={(id) => setCurrentMenuItem(id as ContactFormMenuItems)}
            selectedItemId={currentMenuItem}
          >
            <SideMenu.Item id={ContactFormMenuItems.Basic} title={basicInfoTitle}>
              <WrappedContactBasicInfo
                contact={contact}
                contactFlags={contactFlags}
                source={source}
                isOnboarding={isOnboarding}
                requestPartnerId={resolvedContactPartnerId}
                onContactUpdate={onContactUpdate}
              />
            </SideMenu.Item>
            <SideMenu.Item id={ContactFormMenuItems.Additional} title={additionalInfoTitle}>
              <WrappedContactAdditionalInfo
                contact={contact}
                contactFlags={contactFlags}
                source={source}
                isOnboarding={isOnboarding}
                requestPartnerId={resolvedContactPartnerId}
                onContactUpdate={onContactUpdate}
              />
            </SideMenu.Item>
            {!isNewContact && !isOnboarding && <SideMenu.Separator />}
            <SideMenu.Item
              id={ContactFormMenuItems.UserRights}
              title={ContactEditorFormKeys.userRights}
              isHidden={isOnboarding || isNewContact}
            >
              <WrappedContactFlags
                contact={contact}
                contactFlags={contactFlags}
                type={ProfilingCodeType.UserRights}
                onContactFlagsUpdate={onContactFlagsUpdate!}
              />
            </SideMenu.Item>
            <SideMenu.Item
              id={ContactFormMenuItems.Campaigns}
              title={ContactEditorFormKeys.campaigns}
              isHidden={isOnboarding || isNewContact}
            >
              <WrappedContactFlags
                contact={contact}
                contactFlags={contactFlags}
                type={ProfilingCodeType.Campaigns}
                onContactFlagsUpdate={onContactFlagsUpdate!}
              />
            </SideMenu.Item>
            <SideMenu.Item
              id={ContactFormMenuItems.ToolAccess}
              title={ContactEditorFormKeys.toolAccess}
              isHidden={isOnboarding || isNewContact}
            >
              <WrappedContactFlags
                contact={contact}
                contactFlags={contactFlags}
                type={ProfilingCodeType.Tools}
                onContactFlagsUpdate={onContactFlagsUpdate!}
              />
            </SideMenu.Item>
            {!isNewContact && !isOnboarding && <SideMenu.Separator />}
            <SideMenu.Item
              id={ContactFormMenuItems.ContactRelinking}
              title={ContactEditorFormKeys.contactRelinking}
              isHidden={isOnboarding || isNewContact}
            >
              <WrappedContactRelinking
                contact={contact}
                contactFlags={contactFlags}
                onContactUpdate={onContactUpdate!}
              />
            </SideMenu.Item>
            {isOnboardingSideMenu && <SideMenu.Separator />}
            <SideMenu.Item
              title={contactEditorTranslation.menu.contactRelinking}
              isHidden={!isOnboardingSideMenu}
            >
              <WrappedOnboardingContactRelinking
                contact={contactForRelinkingData}
                requestContact={contact}
                contactFlags={contactFlags}
                source={source}
                onContactUpdate={onContactUpdate!}
                formKey={ContactEditorFormKeys.onboardingContactRelinking}
              />
            </SideMenu.Item>
            <SideMenu.Item
              title={contactEditorTranslation.menu.createReplacementContact}
              isHidden={!isOnboardingSideMenu}
            >
              <WrappedCreateReplacementContact
                contact={contact}
                source={source}
                onClose={onClose}
                onReplacementContactCreate={onReplacementContactCreate}
              />
            </SideMenu.Item>
          </SideMenu>
        </div>
      </div>
    )
  }
)

ContactEditorContent.displayName = 'ContactEditorContent'
